import React, { useState } from "react";
import PageShell from "@/components/PageShell";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, Copy, ExternalLink, KeyRound, Mail } from "lucide-react";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [fallback, setFallback] = useState(null); // { code, discord }
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Use the new manual-reset endpoint: it fires the email AND
      // returns a 7-char A-Z0-9 code as a fallback when admins must approve.
      const { data } = await api.post("/auth/manual-reset-request", { email });
      setDone(true);
      if (data?.available && data?.code) {
        setFallback({ code: data.code, discord: data.discord_founder_url || "" });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Request failed");
    } finally { setLoading(false); }
  };

  const copyCode = async () => {
    if (!fallback?.code) return;
    try {
      await navigator.clipboard.writeText(fallback.code);
      toast.success("Reset code copied to clipboard.");
    } catch (_) {
      toast.error("Could not copy. Please copy manually.");
    }
  };

  return (
    <PageShell noFooter>
      <div className="min-h-[70vh] grid place-items-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="font-mystic text-rose-400 text-sm">記憶の喪失</div>
            <h1 className="font-display text-3xl font-bold text-white mt-1">Forgot Password</h1>
            <p className="text-zinc-400 text-sm mt-2">We'll email a reset link, and provide a backup code in case email fails.</p>
          </div>

          {done && fallback?.code && (
            <div className="mb-5 rounded-xl border border-amber-500/40 bg-amber-950/30 p-4 backdrop-blur" data-testid="manual-reset-fallback" role="alert">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-amber-200 font-semibold text-sm">Manual reset backup code</div>
                  <p className="text-amber-100/80 text-xs mt-1 leading-relaxed">
                    If you can't access the reset email, contact the Founder on Discord with this code.
                    Once approved, return to <Link to={`/reset-password?email=${encodeURIComponent(email)}`} className="underline">this page</Link> to set a new password.
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <code
                      className="flex-1 px-3 py-2 rounded-md bg-black/50 border border-amber-500/30 text-amber-200 font-mono text-lg tracking-[0.25em] text-center select-all"
                      data-testid="manual-reset-code"
                    >
                      {fallback.code}
                    </code>
                    <button type="button" onClick={copyCode} className="px-2 py-2 rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30" title="Copy code" data-testid="manual-reset-copy">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    {fallback.discord && (
                      <a href={fallback.discord} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 underline underline-offset-2" data-testid="manual-reset-discord">
                        <ExternalLink className="w-3 h-3" /> Contact on Discord
                      </a>
                    )}
                    <a href="https://www.facebook.com/KyoukaReimei02" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300 underline underline-offset-2" data-testid="manual-reset-facebook">
                      <ExternalLink className="w-3 h-3" /> Contact on Facebook
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="glass-strong rounded-xl p-7">
            {done ? (
              <div>
                <div className="text-rose-300 text-sm mb-3">
                  If the email exists, a reset link has been sent and a backup code was generated.
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Link to="/login" className="text-zinc-400 hover:text-zinc-200">Back to login</Link>
                  <span className="text-zinc-600">•</span>
                  <Link to={`/reset-password?email=${encodeURIComponent(email)}`} className="text-rose-300 hover:text-rose-200">I have my code</Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-zinc-300 text-xs">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <Input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-black/40 border-white/10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-rose-600 hover:bg-rose-500" data-testid="forgot-submit-btn">
                  {loading ? "Sending…" : "Send Reset Link"}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <Link to="/login" className="text-zinc-400 hover:text-zinc-200">Back to login</Link>
                  <Link to="/reset-password" className="text-rose-400 hover:text-rose-300">I have a reset code</Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

/** Reset page now supports BOTH legacy `token`-based reset and the new `code`-based manual reset. */
export function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const initialToken = sp.get("token") || "";
  const initialEmail = sp.get("email") || "";

  // Mode toggles based on whether a token is present in the URL.
  const [mode, setMode] = useState(initialToken ? "token" : "code");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [token, setToken] = useState(initialToken);
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "token") {
        await api.post("/auth/reset-password", { token, password });
      } else {
        await api.post("/auth/manual-reset-verify", { email, code: code.trim().toUpperCase(), new_password: password });
      }
      setDone(true);
      toast.success("Password updated successfully.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <PageShell noFooter>
      <div className="min-h-[70vh] grid place-items-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="font-mystic text-rose-400 text-sm">儀式の再生</div>
            <h1 className="font-display text-3xl font-bold text-white mt-1">Reset Password</h1>
          </div>
          <div className="glass-strong rounded-xl p-7">
            {done ? (
              <div className="text-rose-300 text-sm">Password updated. <Link to="/login" className="underline">Sign in</Link>.</div>
            ) : (
              <>
                <div className="flex border border-white/10 rounded-md overflow-hidden mb-5">
                  <button
                    type="button"
                    onClick={() => setMode("code")}
                    className={`flex-1 px-3 py-2 text-xs uppercase tracking-wider ${mode === "code" ? "bg-rose-600/20 text-white" : "text-zinc-400 hover:text-zinc-200"}`}
                    data-testid="reset-mode-code"
                  >
                    Manual Code
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("token")}
                    className={`flex-1 px-3 py-2 text-xs uppercase tracking-wider border-l border-white/10 ${mode === "token" ? "bg-rose-600/20 text-white" : "text-zinc-400 hover:text-zinc-200"}`}
                    data-testid="reset-mode-token"
                  >
                    Email Token
                  </button>
                </div>
                <form onSubmit={submit} className="space-y-4">
                  {mode === "code" ? (
                    <>
                      <div>
                        <Label className="text-zinc-300 text-xs">Email</Label>
                        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-black/40 border-white/10" data-testid="reset-email-input" />
                      </div>
                      <div>
                        <Label className="text-zinc-300 text-xs">Reset Code</Label>
                        <div className="relative mt-1.5">
                          <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                          <Input
                            required
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7))}
                            placeholder="e.g. KYO123A"
                            className="pl-10 bg-black/40 border-white/10 font-mono tracking-[0.3em] uppercase"
                            data-testid="reset-code-input"
                            minLength={7}
                            maxLength={7}
                          />
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-1">7-character code provided by the Founder after Discord approval.</p>
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label className="text-zinc-300 text-xs">Token</Label>
                      <Input required value={token} onChange={(e) => setToken(e.target.value)} className="mt-1.5 bg-black/40 border-white/10 font-mono text-xs" data-testid="reset-token-input" />
                    </div>
                  )}
                  <div>
                    <Label className="text-zinc-300 text-xs">New Password</Label>
                    <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-black/40 border-white/10" data-testid="reset-password-input" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-rose-600 hover:bg-rose-500" data-testid="reset-submit-btn">
                    {loading ? "Resetting…" : "Reset Password"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
